import * as React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import SearchField from '../../components/SearchField';
import { useDialogRef, dialogPanelProps } from '../../components/a11y';
import OrgContext from '@/src/Authentication/components/OrgContext';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

/*
 * Orgs admin — every organization on the platform. Create finally has
 * a working button (classic's never rendered — an action/actions prop
 * typo) and edit finally exists at all. "Administer" = switch into the
 * org and use its Organization settings; licenses live on the
 * Licenses page with an org filter.
 */

interface OrgRow {
    id: number;
    name: string;
    logo?: string | null;
}

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

export default function AdminOrgsV2() {
    const { listOrgs, createOrg, updateOrg } = useOrgs();
    const { org: currentOrg } = useUserOrg();
    const { setOrg } = useContext(OrgContext) as any;

    const [rows, setRows] = useState<OrgRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialog, setDialog] = useState<
        { kind: 'create' } | { kind: 'edit'; row: OrgRow } | null
    >(null);
    const [draftName, setDraftName] = useState('');
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);
    const dialogRef = useDialogRef(dialog != null, () => setDialog(null));

    const load = async () => {
        const data = (await listOrgs().catch(() => [])) ?? [];
        setRows(
            [...data].sort((a: OrgRow, b: OrgRow) =>
                (a.name ?? '').localeCompare(b.name ?? ''),
            ),
        );
        setIsLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(
            (r) =>
                r.name?.toLowerCase().includes(q) ||
                String(r.id) === q,
        );
    }, [rows, search]);

    const submit = async () => {
        if (!dialog || busy || !draftName.trim()) return;
        setBusy(true);
        setDialogError(null);
        const ok =
            dialog.kind === 'create'
                ? await createOrg({ name: draftName.trim() }).catch(
                      () => null,
                  )
                : await updateOrg(dialog.row.id, {
                      name: draftName.trim(),
                  }).catch(() => null);
        setBusy(false);
        if (ok) {
            setDialog(null);
            toast.success(
                dialog.kind === 'create' ? 'Organization created' : 'Saved',
            );
            load();
        } else {
            setDialogError("Couldn't save — try again.");
        }
    };

    const switchTo = (row: OrgRow) => {
        setOrg(row);
        /* clean reload — every page's data is org-scoped */
        window.location.assign('/v2/org');
    };

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    return (
        <>
            <SettingsCard
                title="Organizations"
                desc="To administer an org — accounts, whitelabel, templates — switch into it and use its Organization settings."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            {rows.length} organization
                            {rows.length === 1 ? '' : 's'}
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                setDraftName('');
                                setDialogError(null);
                                setDialog({ kind: 'create' });
                            }}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            Add organization
                        </button>
                    </>
                }
            >
                {rows.length > 8 && (
                    <SearchField
                        className="mb-2 w-[220px]"
                        placeholder="Search organizations…"
                        value={search}
                        onChange={setSearch}
                    />
                )}
                {filtered.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        No organizations match.
                    </p>
                )}
                {filtered.map((row) => {
                    const current = row.id === currentOrg?.id;
                    return (
                        <div
                            key={row.id}
                            className="group flex items-center gap-3 border-b border-line py-[9px] text-[12.5px] last:border-b-0"
                        >
                            <span className="flex h-[26px] w-[26px] flex-none items-center justify-center overflow-hidden rounded-[8px] border border-border bg-sunk text-[11px] font-bold">
                                {row.logo ? (
                                    <img
                                        src={row.logo}
                                        alt=""
                                        className="h-full w-full object-contain"
                                    />
                                ) : (
                                    (row.name ?? '?')[0]?.toUpperCase()
                                )}
                            </span>
                            <span className="min-w-0 flex-1 truncate font-semibold">
                                {row.name}
                                {current && (
                                    <span className="ml-2 font-mono text-[9px] font-semibold tracking-[0.1em] text-good">
                                        CURRENT
                                    </span>
                                )}
                            </span>
                            <span className="font-mono text-[10px] text-faint">
                                #{row.id}
                            </span>
                            <span className="flex gap-3 text-[12px] font-semibold opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                                {!current && (
                                    <button
                                        type="button"
                                        onClick={() => switchTo(row)}
                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-brand-ink"
                                    >
                                        switch to
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDraftName(row.name ?? '');
                                        setDialogError(null);
                                        setDialog({ kind: 'edit', row });
                                    }}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                                >
                                    rename
                                </button>
                            </span>
                        </div>
                    );
                })}
            </SettingsCard>

            {dialog && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setDialog(null)}
                >
                    <div
                        ref={dialogRef}
                        {...dialogPanelProps(
                            dialog.kind === 'create'
                                ? 'Add organization'
                                : `Rename ${dialog.row.name}`,
                        )}
                        className="w-full max-w-[380px] rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-3.5">
                            {dialog.kind === 'create'
                                ? 'Add organization'
                                : `Rename ${dialog.row.name}`}
                        </div>
                        <label className={FIELD_LABEL}>Name</label>
                        <input
                            autoFocus
                            className="ps-input mb-4 w-full text-[13px]"
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') submit();
                            }}
                        />
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                disabled={busy || !draftName.trim()}
                                onClick={submit}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {busy
                                    ? 'Saving…'
                                    : dialog.kind === 'create'
                                    ? 'Create organization'
                                    : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setDialog(null)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                        {dialogError && (
                            <p className="mt-2.5 text-[12px] font-semibold text-attention">
                                {dialogError}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
